import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Card, CardContent } from '../../components/ui/card';
import { Phone, MapPin, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['all', 'pending', 'pending_cash_verification', 'confirmed', 'preparing', 'packed', 'delivered'];
const statusColors = { pending: 'bg-yellow-100 text-yellow-800', pending_cash_verification: 'bg-orange-100 text-orange-800', confirmed: 'bg-blue-100 text-blue-800', preparing: 'bg-indigo-100 text-indigo-800', packed: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');

  const fetchOrders = () => {
    setLoading(true);
    const params = filter !== 'all' ? `?status=${filter}` : '';
    api.get(`/admin/orders${params}`).then(r => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchOrders(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusUpdate = async (orderId, status) => {
    try { await api.put(`/admin/orders/${orderId}/status`, { status }); toast.success(`Updated to ${status}`); fetchOrders(); } catch { toast.error('Failed'); }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus || selected.length === 0) return;
    try { await api.put('/admin/orders/bulk-status', { order_ids: selected, status: bulkStatus }); toast.success(`Updated ${selected.length} orders`); setSelected([]); setBulkStatus(''); fetchOrders(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-orders-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl sm:text-3xl font-serif font-medium">Orders</h1><p className="text-sm text-muted-foreground mt-1">{orders.length} orders</p></div>
      </div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {STATUSES.map(s => (
          <Button key={s} data-testid={`order-filter-${s}`} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)} className="rounded-full capitalize text-xs">{s.replace(/_/g, ' ')}</Button>
        ))}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">{selected.length} selected</span>
            <Select value={bulkStatus} onValueChange={setBulkStatus}><SelectTrigger className="w-36 rounded-full"><SelectValue placeholder="Bulk action" /></SelectTrigger><SelectContent><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="preparing">Preparing</SelectItem><SelectItem value="packed">Packed</SelectItem><SelectItem value="delivered">Delivered</SelectItem></SelectContent></Select>
            <Button data-testid="bulk-update-button" size="sm" onClick={handleBulkUpdate} className="rounded-full" disabled={!bulkStatus}>Apply</Button>
          </div>
        )}
      </div>
      <Card className="rounded-xl">
        <CardContent className="pt-0">
          {loading ? <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div> : orders.length === 0 ? <p className="text-center py-20 text-muted-foreground">No orders found</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
                    <TableCell><Checkbox checked={selected.includes(o.order_id)} onCheckedChange={() => setSelected(prev => prev.includes(o.order_id) ? prev.filter(x => x !== o.order_id) : [...prev, o.order_id])} /></TableCell>
                    <TableCell className="font-medium text-sm">{o.order_id}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{o.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{o.customer_email}</p>
                      {(o.customer_phone || o.shipping_address?.phone) && (
                        <p className="text-xs text-primary flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{o.customer_phone || o.shipping_address?.phone}</p>
                      )}
                      {o.shipping_address && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 max-w-[200px]" title={`${o.shipping_address.address}, ${o.shipping_address.city} ${o.shipping_address.pincode}`}>
                          <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{o.shipping_address.address}, {o.shipping_address.city} {o.shipping_address.pincode}</span>
                        </p>
                      )}
                      {(o.location_lat && o.location_lng) && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${o.location_lat},${o.location_lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`map-link-${o.order_id}`}
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
                        >
                          <MapPin className="h-3 w-3" /> Open in Google Maps
                        </a>
                      )}
                    </TableCell>
                    <TableCell><div className="space-y-0.5">{o.items?.slice(0, 2).map((item, i) => <p key={i} className="text-xs text-muted-foreground">{item.product_name} x{item.quantity}</p>)}{o.items?.length > 2 && <p className="text-xs text-muted-foreground">+{o.items.length - 2} more</p>}</div></TableCell>
                    <TableCell className="font-medium">{'\u20B9'}{o.total}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {o.payment_method === 'razorpay' ? <CreditCard className="h-3.5 w-3.5 text-blue-600" /> : <Banknote className="h-3.5 w-3.5 text-green-600" />}
                        <span className="text-xs capitalize">{o.payment_method === 'cash_on_counter' ? 'COC' : o.payment_method || 'COD'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{o.payment_status}</p>
                    </TableCell>
                    <TableCell><Badge className={`${statusColors[o.status] || 'bg-gray-100 text-gray-800'} border-0 rounded-full text-xs capitalize`}>{o.status?.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => handleStatusUpdate(o.order_id, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs rounded-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="preparing">Preparing</SelectItem>
                          <SelectItem value="packed">Packed</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
